import React, { useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsSankey from 'highcharts/modules/sankey';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip
} from '@mui/material';

// Initialize Sankey module
if (typeof Highcharts === 'object') {
    HighchartsSankey(Highcharts);
}

const allPossibleStages = [
    'Applied', 'Contacted', 'Scheduled', 'Interviewed',
    'Selected', 'NotSelected', 'NotConsidered', 'Pending'
];

const SankeyChartSection = ({ applicantData }) => {
    const [selectedStages, setSelectedStages] = useState([
        'Applied', 'Contacted', 'Interviewed', 'Selected', 'NotSelected',
    ]);

    const sankeyOptionsDynamic = useMemo(() => {
        if (!applicantData) return {};
        const sankeyMap = {};
        const addLink = (from, to) => {
            if (!from || !to) return;
            const key = `${from}->${to}`;
            sankeyMap[key] = (sankeyMap[key] || 0) + 1;
        };

        applicantData.forEach(applicant => {
            const actualPath = ['Applied'];
            if (applicant.Contacted) actualPath.push('Contacted');
            if (applicant.Scheduled) actualPath.push('Scheduled');
            if (applicant.Interviewed) actualPath.push('Interviewed');
            const finalStatus = applicant.Activity || 'Pending';
            if (!actualPath.includes(finalStatus)) actualPath.push(finalStatus);

            const filteredPath = actualPath.filter(stage => selectedStages.includes(stage));
            for (let i = 0; i < filteredPath.length - 1; i++) {
                addLink(filteredPath[i], filteredPath[i + 1]);
            }
        });

        const sankeyData = Object.entries(sankeyMap).map(([key, weight]) => {
            const [from, to] = key.split('->');
            return { from, to, weight };
        });

        return {
            title: { text: 'Custom Applicant Flow' },
            series: [{
                keys: ['from', 'to', 'weight'],
                data: sankeyData,
                type: 'sankey',
                name: 'Applicant flow',
                dataLabels: { style: { fontSize: '14px', fontWeight: 'bold' } }
            }],
            credits: { enabled: false },
        };
    }, [applicantData, selectedStages]);

    const handleStageChange = (event) => {
        const { target: { value } } = event;
        setSelectedStages(typeof value === 'string' ? value.split(',') : value);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Applicant Flow Analysis
            </Typography>
            <FormControl sx={{ my: 2, width: '100%' }}>
                <InputLabel id="stage-select-label">Select Stages to Display</InputLabel>
                <Select
                    labelId="stage-select-label"
                    multiple
                    value={selectedStages}
                    onChange={handleStageChange}
                    input={<OutlinedInput label="Select Stages to Display" />}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                                <Chip key={value} label={value} />
                            ))}
                        </Box>
                    )}
                >
                    {allPossibleStages.map((stage) => (
                        <MenuItem key={stage} value={stage}>
                            {stage}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <HighchartsReact highcharts={Highcharts} options={sankeyOptionsDynamic} />
        </Box>
    );
};

export default SankeyChartSection;



